import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { PublicLayout } from './components/PublicLayout';
import { ScrollToTop } from './components/ScrollToTop';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { AppErrorFallback } from './components/ErrorBoundary';
import { RouteSuspenseFallback } from './components/RouteSuspenseFallback';
import { TripPlannerProvider } from './contexts/TripPlannerContext';
import { HomePage } from './pages/HomePage';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AdminLayout } from './pages/admin/AdminLayout';

// Lazy page chunks — everything except HomePage (eager for / first paint).
const PlaceDetailPage = lazy(() => import('./pages/PlaceDetailPage').then((m) => ({ default: m.PlaceDetailPage })));
const DiscoveryPage = lazy(() => import('./pages/DiscoveryPage').then((m) => ({ default: m.DiscoveryPage })));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage').then((m) => ({ default: m.CategoriesPage })));
const NeighborhoodPage = lazy(() => import('./pages/NeighborhoodPage').then((m) => ({ default: m.NeighborhoodPage })));
const NeighborhoodsListPage = lazy(() =>
  import('./pages/NeighborhoodsListPage').then((m) => ({ default: m.NeighborhoodsListPage })),
);
const ItinerariesListPage = lazy(() =>
  import('./pages/ItinerariesListPage').then((m) => ({ default: m.ItinerariesListPage })),
);
const ItineraryDetailPage = lazy(() => import('./pages/ItineraryDetailPage').then((m) => ({ default: m.ItineraryDetailPage })));
const PracticalInfoPage = lazy(() => import('./pages/PracticalInfoPage').then((m) => ({ default: m.PracticalInfoPage })));
const GettingToAlexandriaPage = lazy(() =>
  import('./pages/GettingToAlexandriaPage').then((m) => ({ default: m.GettingToAlexandriaPage })),
);
const ArabicPhrasesPage = lazy(() => import('./pages/ArabicPhrasesPage').then((m) => ({ default: m.ArabicPhrasesPage })));
const EventsListPage = lazy(() => import('./pages/EventsListPage').then((m) => ({ default: m.EventsListPage })));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage').then((m) => ({ default: m.EventDetailPage })));
const EditorialListPage = lazy(() => import('./pages/EditorialListPage').then((m) => ({ default: m.EditorialListPage })));
const EditorialDetailPage = lazy(() =>
  import('./pages/EditorialDetailPage').then((m) => ({ default: m.EditorialDetailPage })),
);
const StreetFoodListPage = lazy(() => import('./pages/StreetFoodListPage').then((m) => ({ default: m.StreetFoodListPage })));
const StreetFoodDetailPage = lazy(() =>
  import('./pages/StreetFoodDetailPage').then((m) => ({ default: m.StreetFoodDetailPage })),
);
const FavoritesPage = lazy(() => import('./pages/FavoritesPage').then((m) => ({ default: m.FavoritesPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })));
const SubmitGemPage = lazy(() => import('./pages/SubmitGemPage').then((m) => ({ default: m.SubmitGemPage })));
const GenericStaticPage = lazy(() => import('./pages/GenericStaticPage').then((m) => ({ default: m.GenericStaticPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const AccountSettingsPage = lazy(() =>
  import('./pages/AccountSettingsPage').then((m) => ({ default: m.AccountSettingsPage })),
);
const ContributionsPage = lazy(() => import('./pages/ContributionsPage').then((m) => ({ default: m.ContributionsPage })));
const TripPlansListPage = lazy(() => import('./pages/TripPlansListPage').then((m) => ({ default: m.TripPlansListPage })));
const TripPlanEditorPage = lazy(() => import('./pages/TripPlanEditorPage').then((m) => ({ default: m.TripPlanEditorPage })));
const SignInPage = lazy(() => import('./pages/auth/SignInPage').then((m) => ({ default: m.SignInPage })));
const SignUpPage = lazy(() => import('./pages/auth/SignUpPage').then((m) => ({ default: m.SignUpPage })));
const ForgotPasswordPage = lazy(() =>
  import('./pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })));
const ResetPasswordPage = lazy(() =>
  import('./pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage })));
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage').then((m) => ({ default: m.AdminOverviewPage })));
const AdminPlacesListPage = lazy(() =>
  import('./pages/admin/AdminPlacesListPage').then((m) => ({ default: m.AdminPlacesListPage })),
);
const AdminPlaceEditorPage = lazy(() =>
  import('./pages/admin/AdminPlaceEditorPage').then((m) => ({ default: m.AdminPlaceEditorPage })),
);
const AdminStreetFoodPage = lazy(() =>
  import('./pages/admin/AdminStreetFoodPage').then((m) => ({ default: m.AdminStreetFoodPage })),
);
const AdminStreetFoodEditorPage = lazy(() =>
  import('./pages/admin/AdminStreetFoodEditorPage').then((m) => ({ default: m.AdminStreetFoodEditorPage })),
);
const AdminNeighborhoodsPage = lazy(() =>
  import('./pages/admin/AdminNeighborhoodsPage').then((m) => ({ default: m.AdminNeighborhoodsPage })),
);
const AdminItinerariesPage = lazy(() =>
  import('./pages/admin/AdminItinerariesPage').then((m) => ({ default: m.AdminItinerariesPage })),
);
const AdminNeighborhoodEditorPage = lazy(() =>
  import('./pages/admin/AdminNeighborhoodEditorPage').then((m) => ({ default: m.AdminNeighborhoodEditorPage })),
);
const AdminItineraryEditorPage = lazy(() =>
  import('./pages/admin/AdminItineraryEditorPage').then((m) => ({ default: m.AdminItineraryEditorPage })),
);
const AdminEditorialPage = lazy(() =>
  import('./pages/admin/AdminEditorialPage').then((m) => ({ default: m.AdminEditorialPage })),
);
const AdminEditorialEditorPage = lazy(() =>
  import('./pages/admin/AdminEditorialEditorPage').then((m) => ({ default: m.AdminEditorialEditorPage })),
);
const AdminCategoriesPage = lazy(() =>
  import('./pages/admin/AdminCategoriesPage').then((m) => ({ default: m.AdminCategoriesPage })),
);
const AdminSettingsPage = lazy(() =>
  import('./pages/admin/AdminSettingsPage').then((m) => ({ default: m.AdminSettingsPage })),
);
const AdminReviewsPage = lazy(() => import('./pages/admin/AdminReviewsPage').then((m) => ({ default: m.AdminReviewsPage })));
const AdminCorrectionsPage = lazy(() =>
  import('./pages/admin/AdminCorrectionsPage').then((m) => ({ default: m.AdminCorrectionsPage })),
);
const AdminVerificationPage = lazy(() =>
  import('./pages/admin/AdminVerificationPage').then((m) => ({ default: m.AdminVerificationPage })),
);
const AdminHeroImagesPage = lazy(() =>
  import('./pages/admin/AdminHeroImagesPage').then((m) => ({ default: m.AdminHeroImagesPage })),
);
const AdminStaticPagesPage = lazy(() =>
  import('./pages/admin/AdminStaticPagesPage').then((m) => ({ default: m.AdminStaticPagesPage })),
);
const AdminStaticPageEditorPage = lazy(() =>
  import('./pages/admin/AdminStaticPageEditorPage').then((m) => ({ default: m.AdminStaticPageEditorPage })),
);
const AdminEventsPage = lazy(() => import('./pages/admin/AdminEventsPage').then((m) => ({ default: m.AdminEventsPage })));
const AdminEventEditorPage = lazy(() =>
  import('./pages/admin/AdminEventEditorPage').then((m) => ({ default: m.AdminEventEditorPage })),
);
const AdminMenusPage = lazy(() => import('./pages/admin/AdminMenusPage').then((m) => ({ default: m.AdminMenusPage })));
const AdminImportPage = lazy(() =>
  import('./pages/admin/AdminImportPage').then((m) => ({ default: m.AdminImportPage })),
);
const AdminImportNewPage = lazy(() =>
  import('./pages/admin/AdminImportNewPage').then((m) => ({ default: m.AdminImportNewPage })),
);
const AdminImportResultsPage = lazy(() =>
  import('./pages/admin/AdminImportResultsPage').then((m) => ({ default: m.AdminImportResultsPage })),
);
const AdminImportReviewPage = lazy(() =>
  import('./pages/admin/AdminImportReviewPage').then((m) => ({ default: m.AdminImportReviewPage })),
);

function toCaughtError(value: unknown): Error {
  return value instanceof Error
    ? value
    : new Error(typeof value === 'string' ? value : 'Unknown error');
}

export function App() {
  return (
    <SentryErrorBoundary
      showDialog={false}
      fallback={({ error, eventId, resetError }) => (
        <AppErrorFallback
          error={toCaughtError(error)}
          eventId={eventId}
          onReload={() => {
            resetError();
            window.location.reload();
          }}
          onHome={() => {
            window.location.href = '/';
          }}
        />
      )}
    >
      <BrowserRouter>
        <ScrollToTop />
        <LanguageProvider>
          <AuthProvider>
            <TripPlannerProvider>
              <Suspense fallback={<RouteSuspenseFallback />}>
                <Routes>
                  {/* Public Routes */}
                  <Route element={<PublicLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/places" element={<DiscoveryPage />} />
                    <Route path="/places/:id" element={<PlaceDetailPage />} />
                    <Route path="/categories" element={<CategoriesPage />} />
                    <Route path="/neighborhoods" element={<NeighborhoodsListPage />} />
                    <Route path="/neighborhoods/:slug" element={<NeighborhoodPage />} />
                    <Route path="/itineraries" element={<ItinerariesListPage />} />
                    <Route path="/itinerary/:id" element={<ItineraryDetailPage />} />
                    <Route path="/practical-info" element={<PracticalInfoPage />} />
                    <Route path="/getting-here" element={<GettingToAlexandriaPage />} />
                    <Route path="/arabic-phrases" element={<ArabicPhrasesPage />} />
                    <Route path="/events" element={<EventsListPage />} />
                    <Route path="/events/:id" element={<EventDetailPage />} />
                    <Route path="/street-food" element={<StreetFoodListPage />} />
                    <Route path="/street-food/:id" element={<StreetFoodDetailPage />} />
                    <Route path="/editorial" element={<EditorialListPage />} />
                    <Route path="/editorial/:slug" element={<EditorialDetailPage />} />
                    <Route path="/favorites" element={<FavoritesPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/submit-gem" element={<SubmitGemPage />} />
                    <Route path="/p/:slug" element={<GenericStaticPage />} />
                    {/* Direct routes for legal pages */}
                    <Route path="/terms" element={<GenericStaticPage />} />
                    <Route path="/privacy" element={<GenericStaticPage />} />
                    <Route path="/terms-of-use" element={<GenericStaticPage />} />
                    <Route path="/u/:username" element={<ProfilePage />} />
                    <Route path="/account/settings" element={<AccountSettingsPage />} />
                    <Route path="/account/contributions" element={<ContributionsPage />} />
                    <Route path="/account/plans" element={<TripPlansListPage />} />
                    <Route path="/account/plans/:planId" element={<TripPlanEditorPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>

                  {/* Auth routes — standalone, no public chrome. */}
                  <Route path="/auth/signin" element={<SignInPage />} />
                  <Route path="/auth/signup" element={<SignUpPage />} />
                  <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

                  {/* Admin Standalone Route */}
                  <Route path="/admin/login" element={<AdminLoginPage />} />

                  {/* Admin Dashboard Routes */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminOverviewPage />} />
                    <Route path="places" element={<AdminPlacesListPage />} />
                    <Route path="places/new" element={<AdminPlaceEditorPage />} />
                    <Route path="places/:id/edit" element={<AdminPlaceEditorPage />} />
                    <Route path="street-food" element={<AdminStreetFoodPage />} />
                    <Route path="street-food/new" element={<AdminStreetFoodEditorPage />} />
                    <Route path="street-food/:id/edit" element={<AdminStreetFoodEditorPage />} />
                    <Route path="neighborhoods" element={<AdminNeighborhoodsPage />} />
                    <Route path="neighborhoods/new" element={<AdminNeighborhoodEditorPage />} />
                    <Route path="neighborhoods/:id/edit" element={<AdminNeighborhoodEditorPage />} />
                    <Route path="itineraries" element={<AdminItinerariesPage />} />
                    <Route path="itineraries/new" element={<AdminItineraryEditorPage />} />
                    <Route path="itineraries/:id/edit" element={<AdminItineraryEditorPage />} />
                    <Route path="editorial" element={<AdminEditorialPage />} />
                    <Route path="editorial/:slug" element={<AdminEditorialEditorPage />} />
                    <Route path="categories" element={<AdminCategoriesPage />} />
                    <Route path="menus" element={<AdminMenusPage />} />
                    <Route path="import/jobs/:id/review" element={<AdminImportReviewPage />} />
                    <Route path="import/jobs/:id" element={<AdminImportResultsPage />} />
                    <Route path="import/new" element={<AdminImportNewPage />} />
                    <Route path="import" element={<AdminImportPage />} />
                    <Route path="static-pages" element={<AdminStaticPagesPage />} />
                    <Route path="static-pages/:slug" element={<AdminStaticPageEditorPage />} />
                    <Route path="events" element={<AdminEventsPage />} />
                    <Route path="events/:id" element={<AdminEventEditorPage />} />
                    <Route path="hero-images" element={<AdminHeroImagesPage />} />
                    <Route path="reviews" element={<AdminReviewsPage />} />
                    <Route path="corrections" element={<AdminCorrectionsPage />} />
                    <Route path="verification" element={<AdminVerificationPage />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                  </Route>
                </Routes>
              </Suspense>
            </TripPlannerProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </SentryErrorBoundary>
  );
}
